<script setup lang="ts">
/**
 * PInput — champ de saisie textuel du design system PreLLM.
 *
 * Pattern Revelio : label mono uppercase tracking-wider AU-DESSUS du champ,
 * input bg bg + bordure line, focus border ink-3 (PAS de box-shadow ring
 * qui pollue le minimalisme). Variant `surface` pour les inputs dans des
 * cards où le contraste bg/surface n'existe pas (utilise bg-bg quand même).
 *
 * Support v-model standard (string). Pour textarea, voir PTextarea (V1.5).
 */
interface Props {
    modelValue?: string
    label?: string
    placeholder?: string
    type?: 'text' | 'email' | 'password' | 'search' | 'url' | 'number'
    size?: 'sm' | 'md' | 'lg'
    disabled?: boolean
    error?: string
    hint?: string
    required?: boolean
    id?: string
    autocomplete?: string
    /** Texte court à droite du label (ex: "Optionnel", "32 max"). */
    suffix?: string
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    type: 'text',
    size: 'md',
    disabled: false,
    required: false,
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: string): void
    (e: 'blur', event: FocusEvent): void
}>()

function onInput(event: Event) {
    const target = event.target as HTMLInputElement
    emit('update:modelValue', target.value)
}

const inputId = props.id ?? `p-input-${crypto.randomUUID().slice(0, 8)}`
</script>

<template>
    <div :class="['p-input', `p-input--${size}`, { 'p-input--error': error, 'p-input--disabled': disabled }]">
        <div v-if="label || suffix" class="p-input__label-row">
            <label v-if="label" :for="inputId" class="p-input__label">
                {{ label }}
                <span v-if="required" aria-hidden="true" class="p-input__required">*</span>
            </label>
            <span v-if="suffix" class="p-input__suffix">{{ suffix }}</span>
        </div>
        <input
            :id="inputId"
            :value="modelValue"
            :type="type"
            :placeholder="placeholder"
            :disabled="disabled"
            :required="required"
            :autocomplete="autocomplete"
            :aria-invalid="error ? 'true' : 'false'"
            :aria-describedby="error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined"
            class="p-input__field"
            @input="onInput"
            @blur="emit('blur', $event)"
        />
        <p v-if="error" :id="`${inputId}-error`" class="p-input__error">{{ error }}</p>
        <p v-else-if="hint" :id="`${inputId}-hint`" class="p-input__hint">{{ hint }}</p>
    </div>
</template>

<style scoped>
.p-input {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.p-input__label-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--p-space-2);
}

.p-input__label {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    font-weight: var(--p-font-weight-medium);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
    color: var(--p-color-ink-4);
}

.p-input__required {
    color: var(--p-color-brick);
    margin-left: 4px;
}

.p-input__suffix {
    font-family: var(--p-font-mono);
    font-size: var(--p-text-2xs);
    color: var(--p-color-ink-5);
    text-transform: uppercase;
    letter-spacing: var(--p-tracking-wider);
}

.p-input__field {
    width: 100%;
    background: var(--p-color-bg);
    color: var(--p-color-ink);
    border: 1px solid var(--p-color-line);
    border-radius: var(--p-radius-sm);
    font-family: var(--p-font-sans);
    transition: border-color var(--p-motion-fast) var(--p-ease-out);
}

.p-input__field::placeholder {
    color: var(--p-color-ink-5);
}

.p-input__field:focus-visible {
    outline: none;
    border-color: var(--p-color-ink-3);
}

.p-input__field:disabled {
    background: var(--p-color-surface-alt);
    color: var(--p-color-ink-5);
    cursor: not-allowed;
}

.p-input--sm .p-input__field {
    padding: 6px 10px;
    font-size: var(--p-text-xs);
}
.p-input--md .p-input__field {
    padding: 8px 12px;
    font-size: var(--p-text-sm);
}
.p-input--lg .p-input__field {
    padding: 10px 14px;
    font-size: var(--p-text-base);
}

.p-input--error .p-input__field {
    border-color: var(--p-color-brick);
}

.p-input__error {
    margin: 0;
    color: var(--p-color-brick);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}

.p-input__hint {
    margin: 0;
    color: var(--p-color-ink-4);
    font-size: var(--p-text-xs);
    line-height: var(--p-line-relaxed);
}
</style>
